import { ChangeEvent, FC, useEffect, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { AxiosError } from 'axios'
import { Card } from 'components'
import { useAuth, useLoading, useModal, usePersistForm, useToggle } from 'hooks'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import {
  ParamFaleConoscoResponseDto,
  TalkWithUsServices
} from 'services/NGT/Parameterization'
import TalkToUsService, {
  ITalkToUsRequestDto,
  ITalkToUsResponseDto
} from 'services/NGT/TalkToUs'
import { SPACING, COLORS, OLDYUP as YUP } from 'utils'

import FilterSearchTalkToUs from './FilterSearchTalkToUs'
import TableSearchTalkToUs from './TableSearchTalkToUs'
import * as yup from 'yup'

export interface TalkToUsSearchProps {
  dtEnvioFim: string
  dtEnvioInicio: string
  flLiberado: boolean
  idMotivo: number
}

export interface ISearchTalkToUs {
  setSelectedTalkToUs: (idFaleConosco: number) => void
}

const SCHEMA = yup.object({
  idMotivo: yup.number().notRequired().nullable(),
  dtEnvioInicio: yup
    .date()
    .notRequired()
    .nullable()
    .typeError('Digite uma data válida.'),
  dtEnvioFim: yup
    .date()
    .notRequired()
    .nullable()
    .typeError('Digite uma data válida.')
    .min(
      yup.ref('dtEnvioInicio'),
      'A data de envio fim deve ser maior que data envio inicio.'
    ),
  flLiberado: yup.bool().notRequired().nullable()
})

const SearchTalkToUs: FC<ISearchTalkToUs> = ({ setSelectedTalkToUs }) => {
  const { showModal } = useModal()
  const { setLoading } = useLoading()
  const { user } = useAuth()

  const [parametrizationTalkToUs, setParametrizationTalkToUs] = useState<
    ParamFaleConoscoResponseDto[]
  >([])
  const [page, setPage] = useState<number>(1)
  const [pageCount, setPageCount] = useState<number>(1)
  const [rowsPerPage, setRowsPerPage] = useState<number>(15)
  const [changeEffect, toggleChangeEffect] = useToggle()

  const [talkToUsData, setTalkToUsData] = useState<ITalkToUsResponseDto[]>([])

  const {
    reset,
    watch,
    setValue,
    formState: { errors },
    control,
    handleSubmit
  } = useForm<TalkToUsSearchProps>({
    resolver: yupResolver(SCHEMA),
    defaultValues: {
      dtEnvioFim: null,
      dtEnvioInicio: null,
      flLiberado: null,
      idMotivo: 0
    }
  })

  usePersistForm('supplier_talk_to_us_search', {
    setValue,
    watch
  })

  const dtEnvioInicio = watch('dtEnvioInicio')
  const dtEnvioFim = watch('dtEnvioFim')

  // const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
  //   setSearchParams((oldData) => {
  //     return {
  //       ...oldData,
  //       [event.target.name]: event.target.value
  //     }
  //   })
  //   setValue(event.target.name, event.target.value)
  // }

  const areDatesValid = (): boolean => {
    if (
      moment(dtEnvioInicio).diff(moment(dtEnvioFim)) > 0 ||
      (moment(dtEnvioInicio).isValid() && !moment(dtEnvioFim).isValid()) ||
      (!moment(dtEnvioInicio).isValid() && moment(dtEnvioFim).isValid())
    ) {
      return false
    }
    return (
      moment(dtEnvioInicio || moment()).isValid() &&
      moment(dtEnvioFim || moment()).isValid()
    )
  }

  const handleOnClear = () => {
    reset()
    toggleChangeEffect()
    handleSearch(watch())
  }

  const handleSeachParametrizacao = () => {
    setLoading('loading')

    TalkWithUsServices()
      .getAllAutocomplete()
      .then(({ data }) => {
        if (data) {
          setParametrizationTalkToUs(data)
        }
      })
      .catch((error) => {
        const axiosError = error as AxiosError

        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message:
            axiosError.message ||
            'Não foi possivel obter as parametrizações de fale conosco',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
      .finally(() => setLoading('idle'))
  }

  const handleSearch = (
    form: TalkToUsSearchProps = {} as TalkToUsSearchProps
  ) => {
    setLoading('loading')

    const requestData: ITalkToUsRequestDto = {
      idMotivo: form.idMotivo ?? 0,
      flLiberado: form.flLiberado ?? null,
      ...(form.dtEnvioInicio && {
        dtEnvioInicio: moment(form.dtEnvioInicio).toISOString()
      }),
      ...(form.dtEnvioFim && {
        dtEnvioFim: moment(form.dtEnvioFim).toISOString()
      }),
      idUsuario: user.IdUsuario,
      pageNumber: page,
      pageSize: rowsPerPage
    }

    TalkToUsService()
      .getAllPaginated(requestData)
      .then(({ data }) => {
        if (data) {
          setTalkToUsData(data.items)
          setPageCount(data.pageCount)
        }
      })
      .catch((error) => {
        const axisoError = error as AxiosError
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message:
            axisoError.message ||
            'Não foi possivel obter as mensagens de fale conosco',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
      .finally(() => setLoading('idle'))
  }

  // const handleOnSearchButton = () => {
  //   if (page === 1) {
  //     handleSearch()
  //     return
  //   }
  //   setPage(1)
  // }

  // useEffect(() => {
  //   const subs = watch(() => trigger())
  //   return () => subs.unsubscribe()
  // }, [watch])

  useEffect(() => {
    handleSeachParametrizacao()
  }, [])

  useEffect(() => {
    handleSearch(watch())
  }, [page, rowsPerPage])

  // useEffect(() => {
  //   handleOnSearchButton()
  // }, [changeEffect])

  return (
    <>
      <Card
        marginLeft={SPACING.spacing20}
        marginRight={SPACING.spacing20}
        padding={SPACING.spacing20}
        boxShadow={`0 0 1em ${COLORS.lightGray}`}
      >
        <FilterSearchTalkToUs
          control={control}
          handleSubmit={handleSubmit}
          handleOnSearch={handleSearch}
          areDatesValid={areDatesValid}
          handleOnClear={handleOnClear}
          parametrizationTalkToUs={parametrizationTalkToUs}
          errors={errors}
        // searchParams={searchParams}
        // handleOnChange={handleOnChange}
        />
        <TableSearchTalkToUs
          tableData={talkToUsData}
          page={page}
          setSelectedTalkToUs={setSelectedTalkToUs}
          areDatesValid={areDatesValid}
          totalItems={pageCount}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
        />
      </Card>
    </>
  )
}

export default SearchTalkToUs
